/**
 * CafeROIIntro — Bumper animado "Café com teu ROI"
 * Dark bg | Logo SVG animado | 90 frames = 3s @30fps
 */
import React from "react";
import { AbsoluteFill, interpolate, spring, useCurrentFrame } from "remotion";

const CLAMP = { extrapolateLeft: "clamp", extrapolateRight: "clamp" } as const;
const sp = (f: number, delay: number, cfg = { damping: 20, stiffness: 100 }) =>
  spring({ frame: f - delay, fps: 30, config: cfg });

const GOLD = "#C9851C";
const TEAL = "#2EAA9B";
const WHITE = "#FFFFFF";
const BG = "#0A0A0A";
const CX = 540;
const CY = 730;
const R_OUTER = 240;
const R_PIE   = 195;

const arcPath = (cx: number, cy: number, r: number, startDeg: number, endDeg: number) => {
  const toRad = (d: number) => ((d - 90) * Math.PI) / 180;
  const s = { x: cx + r * Math.cos(toRad(startDeg)), y: cy + r * Math.sin(toRad(startDeg)) };
  const e = { x: cx + r * Math.cos(toRad(endDeg)),   y: cy + r * Math.sin(toRad(endDeg))   };
  const large = endDeg - startDeg > 180 ? 1 : 0;
  return `M ${cx} ${cy} L ${s.x} ${s.y} A ${r} ${r} 0 ${large} 1 ${e.x} ${e.y} Z`;
};

export const CafeROIIntro: React.FC = () => {
  const f = useCurrentFrame();

  const bgOpacity  = interpolate(f, [0, 10], [0, 1], CLAMP);
  const ringProg   = interpolate(f, [6, 38], [0, 360], CLAMP);
  const goldProg   = interpolate(f, [18, 52], [0, 270], CLAMP);
  const tealProg   = interpolate(f, [32, 60], [0, 90], CLAMP);
  const fanOpacity = interpolate(f, [44, 62], [0, 1], CLAMP);
  const handleSp   = sp(f, 50);
  const textTop    = sp(f, 55, { damping: 18, stiffness: 120 });
  const textROI    = sp(f, 63, { damping: 16, stiffness: 140 });
  const taglineOp  = interpolate(f, [72, 85], [0, 1], CLAMP);

  const fanAngles = [-78, -63, -48, -33, -18, -3];
  const circ = 2 * Math.PI * R_OUTER;
  const ringDash = interpolate(ringProg, [0, 360], [0, circ], CLAMP);

  const toRad = (d: number) => ((d - 90) * Math.PI) / 180;

  return (
    <AbsoluteFill style={{ background: BG, opacity: bgOpacity }}>
      <svg width={1080} height={1920} style={{ position: "absolute", top: 0, left: 0 }}>

        {/* outer ring draw-on */}
        <circle
          cx={CX} cy={CY} r={R_OUTER}
          fill="none" stroke={WHITE} strokeWidth={18}
          strokeDasharray={`${ringDash} ${circ}`}
          strokeLinecap="round"
          transform={`rotate(-90 ${CX} ${CY})`}
        />

        {/* gold segment — 90° to 360° (270° arc, clockwise from right) */}
        {goldProg > 0.5 && (
          <path d={arcPath(CX, CY, R_PIE, 90, 90 + goldProg)} fill={GOLD} />
        )}

        {/* teal segment — 0° to 90° (top-right quadrant) */}
        {tealProg > 0.5 && (
          <path d={arcPath(CX, CY, R_PIE, 0, tealProg)} fill={TEAL} />
        )}

        {/* fan lines inside teal */}
        <g opacity={fanOpacity}>
          {fanAngles.map((deg, i) => (
            <line
              key={i}
              x1={CX} y1={CY}
              x2={CX + R_PIE * Math.cos(toRad(deg))}
              y2={CY + R_PIE * Math.sin(toRad(deg))}
              stroke={BG} strokeWidth={2.5} opacity={0.7}
            />
          ))}
        </g>

        {/* inner border ring */}
        <circle
          cx={CX} cy={CY} r={R_PIE}
          fill="none" stroke={WHITE} strokeWidth={5}
          opacity={interpolate(f, [30, 50], [0, 1], CLAMP)}
        />

        {/* cup handle */}
        <path
          d={`M ${CX + R_OUTER + 10} ${CY - 50} C ${CX + R_OUTER + 80} ${CY - 50} ${CX + R_OUTER + 80} ${CY + 50} ${CX + R_OUTER + 10} ${CY + 50}`}
          fill="none" stroke={WHITE} strokeWidth={16} strokeLinecap="round"
          opacity={handleSp}
        />

        {/* center dot */}
        <circle
          cx={CX} cy={CY} r={6} fill={WHITE}
          opacity={interpolate(f, [35, 50], [0, 1], CLAMP)}
        />
      </svg>

      {/* "café com teu" */}
      <div style={{
        position: "absolute",
        top: CY + R_OUTER + 52,
        left: 0, right: 0,
        textAlign: "center",
        fontFamily: "'Geist', 'Inter', system-ui, sans-serif",
        fontSize: 42,
        fontWeight: 300,
        letterSpacing: "0.22em",
        textTransform: "lowercase",
        color: WHITE,
        opacity: textTop,
        transform: `translateY(${interpolate(textTop, [0, 1], [24, 0], CLAMP)}px)`,
      }}>
        café com teu
      </div>

      {/* "ROI" */}
      <div style={{
        position: "absolute",
        top: CY + R_OUTER + 108,
        left: 0, right: 0,
        textAlign: "center",
        fontFamily: "'Geist', 'Inter', system-ui, sans-serif",
        fontSize: 148,
        fontWeight: 900,
        letterSpacing: "-0.02em",
        color: GOLD,
        opacity: textROI,
        transform: `scale(${interpolate(textROI, [0, 1], [0.82, 1], CLAMP)})`,
      }}>
        ROI
      </div>

      {/* tagline */}
      <div style={{
        position: "absolute",
        top: CY + R_OUTER + 310,
        left: 0, right: 0,
        textAlign: "center",
        fontFamily: "'Geist Mono', monospace",
        fontSize: 20,
        fontWeight: 400,
        letterSpacing: "0.28em",
        textTransform: "uppercase",
        color: "rgba(255,255,255,0.40)",
        opacity: taglineOp,
      }}>
        @guilhermeribeiro.ai
      </div>
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
